import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { TabPanel, TabContext } from '@mui/lab'
import { PlusIcon } from 'lucide-react'

import { Button, Tabs } from '@repo/ui'
// `SegmentedTabs` import removed with the VRG-470 trial swap below; restore it alongside the
// component if the pill wins.
import { StickyFormHeader } from 'components'
import { useSettingsStickyOffset } from 'pages/settings/SettingsPage/settingsStickyOffset'
import { ROUTES } from 'routes'

import { Overview, Invoices, PaymentMethods } from './components'

// Drive the tab from the URL so deep-links / refresh land on the right tab
// (/billing → overview, /billing/invoices → invoices, /billing/payment-methods).
const TAB_ROUTES: Record<string, string> = {
  overview: ROUTES.SETTINGS_COMPANY_BILLING,
  invoices: ROUTES.SETTINGS_COMPANY_INVOICES,
  'payment-methods': ROUTES.SETTINGS_COMPANY_PAYMENT_METHODS,
}
const tabFromPath = (pathname: string): string => {
  if (pathname.startsWith(ROUTES.SETTINGS_COMPANY_INVOICES)) return 'invoices'
  if (pathname.startsWith(ROUTES.SETTINGS_COMPANY_PAYMENT_METHODS)) return 'payment-methods'
  return 'overview'
}

export const BillingPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [tab, setTab] = useState(() => tabFromPath(location.pathname))
  // Keep the tab in sync when the URL changes (deep-link, back/forward, left-nav).
  useEffect(() => {
    setTab(tabFromPath(location.pathname))
  }, [location.pathname])

  const handleTabChange = (value: string) => {
    setTab(value)
    const route = TAB_ROUTES[value]
    if (route && location.pathname !== route) navigate(route)
  }
  // Lifted so the tab's primary action can live in the Header's rightComponents
  // (parity with the Roles & Permissions page) instead of on a separate row below
  // the tabs — which was pushing the Payment methods content down.
  const [openAddPaymentMethod, setOpenAddPaymentMethod] = useState(false)

  /**
   * Where the sticky block pins. NOT 0 — this page renders inside the settings shell, whose
   * own "Settings" header is already sticky at the top of the scrollport, and the shell header
   * grows by its back-link row when a cross-surface referrer brought the user in. Same call
   * `SupportPage` and `FieldsPage` make; see `settingsStickyOffset.ts`.
   */
  const stickyTop = useSettingsStickyOffset()

  return (
    <>
      {/*
        STICKY, matching Support / My requests / Fields. Billing used a plain `Header`, so the
        title and the tab row scrolled away — on the Overview tab in particular, which runs
        several viewports (meter, chart, tier schedule, stat tiles, queries), you lost both the
        page identity and the means to switch tabs and had to scroll back up to get them.

        The tabs ride INSIDE the block as `children` rather than sitting in the flow beneath it,
        which is the whole point: a tab control you have to scroll back up to reach is not doing
        its job. Same arrangement, same component, as the pages above (§36).
      */}
      <StickyFormHeader
        title="Billing"
        // `nested`: this sits under the settings page's own "Settings" header, so the wayfinding
        // row it would otherwise reserve is empty space that pushed this title 20px below the
        // non-selfTitled sections' (145.9px vs 125.9px, measured).
        nested
        top={stickyTop}
        actions={
          tab === 'payment-methods' ? (
            <Button
              variant="primary"
              disableElevation
              startIcon={<PlusIcon strokeWidth={1} />}
              onClick={() => setOpenAddPaymentMethod(true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              data-tour="billing-add-payment-method"
            >
              Add payment method
            </Button>
          ) : undefined
        }
      >
        {/* TRIAL (VRG-470) — the underline `@repo/ui` Tabs in place of the pill
            `SegmentedTabs`, so the two can be compared side by side against the certificate
            detail page (Product · Provenance · Scans · Activity), which already uses underline.
            The platform currently runs both: underline on 25 surfaces, segmented on 11.

            NOT a decision — a swap staged to be looked at, and reverted in one commit if the
            pill wins. Deliberately presentation-only: same tabs, same order, same values, same
            `onChange`; `SegmentedTabs` and its other 10 consumers are untouched.

            `redirect={false}` + a controlled `value` is required here. The default `redirect`
            mode treats a tab's value as a route and renders it as a `Link`, but this page's
            values are state keys ('overview'), not paths — routing is `handleTabChange`'s job
            via TAB_ROUTES. Controlled also bypasses the component's own pathname-matching,
            which would mis-resolve for the same reason.

            The TabPanels below keep their own TabContext. Tabs renders an internal one for its
            `children`, and it is given none — the panels are siblings of the sticky block, not
            descendants of the tab list, so they cannot share it. */}
        <Tabs
          redirect={false}
          value={tab}
          onChange={handleTabChange}
          tabs={[{
            label: 'Overview',
            value: 'overview',
          },{
            label: 'Invoices',
            value: 'invoices',
          },{
            label: 'Payment methods',
            value: 'payment-methods',
          }]}/>
      </StickyFormHeader>
        <TabContext value={tab}>
          {/*
            ALL THREE TABS LAND 32px BELOW THE TAB ROW. The per-tab `pt` values DIFFER
            because each tab's content carries a different amount of its own leading, and the
            panel makes up the remainder. Read the arithmetic before changing one:

              tab              sticky pb   panel pt   content's own              total
              Overview            16     +   8      +  8 (BillingOverview pt:2)  = 32
              Invoices            16     +   0      + 16 (ActionLine mt:4)        = 32
              Payment methods     16     +  16      +  0                          = 32

            The 16px of `pb` is `StickyFormHeader`'s `bottomGap`, common to all three.

            32 is Invoices' number, and Invoices is the reference because its 16 comes from the
            shared `Table`'s own toolbar spacing — the value every list page in the app already
            uses. Matching it here keeps that standard rather than reaching into the shared
            Table and moving every list page to fix one tab.

            ⚠️ THIS HAS DRIFTED TWICE, both times because a fix was applied at one end while
            another already existed at the other. `pt: 4` was added here on the premise that the
            Table contributed 32 (it is 16 — `ActionLine`'s `mt: 4`), while PaymentMethods
            separately carried `pt: 8`, summing that tab to 64. The two errors cancelled in the
            previous comment's conclusion, which is why it read as settled. If a tab looks wrong,
            measure from the tab row to the first painted block and correct the row of this table
            it belongs to — do not add a compensating `pt` at a third site.
          */}
          <TabPanel value="overview" sx={{p: 0, pt: 2}} data-tour="billing-overview">
            <Overview />
          </TabPanel>
          <TabPanel value="invoices" sx={{p: 0}}>
           <Invoices />
          </TabPanel>
          <TabPanel value="payment-methods" sx={{p: 0, pt: 4}}>
            <PaymentMethods
              openCreateModal={openAddPaymentMethod}
              onCloseCreateModal={() => setOpenAddPaymentMethod(false)}
            />
          </TabPanel>
        </TabContext>
    </>
  )
}
